import {
  ChartConstraints,
  getParsedCrudData,
  preFormatForm,
  withErrorBoundary,
  getTranslatedText,
  validateForm,
  toast,
  useActivityTracker
} from '@nile/components';
import ChartConfig from '@nile/reports/src/modules/charts/components/subpages/ChartConfig';
import ChartDetail from '@nile/reports/src/modules/charts/components/subpages/ChartDetail';
import ChartHeader from '@nile/reports/src/modules/charts/components/subpages/ChartHeader';
import { ChartContextProvider } from '@nile/reports/src/modules/charts/contexts/Chart';
import {
  CUSTOM_VIEW,
  DEFAULT_VIEW,
  VIEW_MODE
} from '@nile/reports/src/modules/home/constants';
import { cloneDeep, isEmpty, isEqual, omit, pick } from 'lodash';
import {
  getPossibleChartTypes,
  getDefaultChartType
} from '@nile/reports/src/modules/charts/utils/chart-transformation';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Banner, Button } from '@superops/ui-design-system';
import { addWidget } from '@nile/reports/src/modules/charts/utils/layoutHelper';
import { SVGIcon } from '@superops/super-icons';
import { Modal } from './styles';

const getContextValue = (data, intl) => {
  return {
    chartId: data?.chartId || null,
    groupBy: data?.groupBy || null,
    addMetrics: [],
    updateMetrics: [],
    deleteMetrics: [],
    entity: data?.entity || 'Ticket',
    metrics: data?.metrics || [
      {
        name: `${getTranslatedText(intl, 'reports.chart.metric.untitled')} - 1`,
        attribute: null,
        metricFunction: null,
        filterCondition: null,
        metricUnit: null,
        metricOrder: 1
      }
    ],
    parentMetrics: data?.metrics || [],
    defaultChartType: data?.defaultChartType || null,
    systemChart: data?.systemChart || false,
    internalName: data?.internalName || false
  };
};

const pickedVariable = [
  'chartId',
  'name',
  'description',
  'groupBy',
  'metrics',
  'defaultChartType'
];

class ChartSheet extends Component {
  constructor(props) {
    super(props);
    this.state = {
      oldProps: props,
      chartData: getContextValue(props.data, props.intl),
      chartHeaders: {
        name:
          props?.data?.name ||
          getTranslatedText(props?.intl, 'reports.widget.title.untitled'),
        description:
          props?.data?.description ||
          getTranslatedText(props?.intl, 'reports.chart.description.untitled')
      },
      chartLoading: true,
      submitActionLoading: false,
      edited: false,
      errors: null,
      isFullScreen: false,
      isTableExpanded: false
    };
  }

  static getDerivedStateFromProps(props, state) {
    const pickedState =
      state.oldProps.data && pick(state.oldProps.data, pickedVariable);
    const pickedProps = props.data && pick(props.data, pickedVariable);

    if (!isEqual(pickedProps, pickedState)) {
      return {
        ...state,
        oldProps: props,
        chartData: getContextValue(props.data, props.intl)
      };
    }
  }

  updateState = (attr, value) => {
    this.setState((prevState) => ({
      ...prevState,
      [attr]: value
    }));
  };

  checkIsEqual = (original, current) => {
    const originalData = original;
    const currentData = current;
    const originalMetrics = originalData.metrics?.map((met) =>
      met.filterCondition?.operands?.length
        ? omit(met, ['name'])
        : omit({ ...met, filterCondition: null }, ['name'])
    );

    const currentMetrics = currentData.metrics?.map((met) =>
      met.filterCondition?.operands?.length
        ? omit(met, ['name'])
        : omit({ ...met, filterCondition: null }, ['name'])
    );

    if (originalMetrics && originalMetrics.length) {
      originalData.metrics = originalMetrics;
    }
    if (currentMetrics && currentMetrics.length) {
      currentData.metrics = currentMetrics;
    }

    const isSame = isEqual(originalData, currentData);
    return isSame;
  };

  isSameData = () => {
    const originalData = pick(cloneDeep(this.props.data), [
      'chartId',
      'entity',
      'groupBy',
      'metrics'
    ]);
    const currentData = pick(cloneDeep(this.state.chartData), [
      'chartId',
      'entity',
      'groupBy',
      'metrics'
    ]);
    const same = this.checkIsEqual(originalData, currentData);
    return same;
  };

  isUpdated = () => {
    const dataUpdated = getContextValue(
      cloneDeep(this.props.data),
      this.props.intl
    );
    const originalData = pick(dataUpdated, [
      'chartId',
      'entity',
      'groupBy',
      'metrics',
      'name',
      'description'
    ]);

    const currentData = pick(cloneDeep(this.state.chartData), [
      'chartId',
      'entity',
      'groupBy',
      'metrics',
      'name',
      'description'
    ]);
    const updated = this.checkIsEqual(originalData, currentData);
    return updated;
  };

  validateChartForm = () => {
    const updatedChartData = cloneDeep({
      ...this.state.chartHeaders,
      ...this.state.chartData
    });

    const validationResult = validateForm(
      preFormatForm(updatedChartData),
      ChartConstraints
    );

    let validationError = null;

    if (validationResult) {
      validationError = validationResult;
    }

    const nameArr = updatedChartData.metrics.map((metrix) => metrix.name);

    nameArr.map((name, idx) => {
      if (nameArr.indexOf(name) !== idx) {
        validationError = {
          metrics: [
            {
              errors: {
                ...validationError?.metrics?.[0]?.errors,
                [idx]: {
                  error: {
                    ...validationError?.metrics?.[0]?.errors?.[idx]?.error,
                    name: ['Name already exist']
                  }
                }
              }
            }
          ]
        };
      }
    });

    if (validationError) {
      this.updateState('errors', validationError);
      return false;
    }

    this.updateState('errors', null);
    return true;
  };

  onLayoutChange = async (clonedChartId, chartData = {}, clone = false) => {
    if (isEmpty(chartData)) return;

    if (clone) {
      const updatedLayout = await addWidget({
        layout: this.props.reportContext?.layout,
        widget: chartData
      });
      const data = this.props.reportContext.activeReport;
      let updatedClonedChart = cloneDeep(data.charts);
      updatedClonedChart = [
        ...updatedClonedChart,
        omit(chartData, ['createdBy', 'createdTime'])
      ];
      this.props?.reportContext.setActiveReport({
        ...data,
        charts: updatedClonedChart
      });
      this.props.reportContext.dispatch({
        type: 'updateLayout',
        layout: updatedLayout
      });
      return;
    }
    const findElement = this.props.reportContext?.layout?.find(
      (el) => el?.chart?.chartId === clonedChartId
    );
    const data = this.props.reportContext.activeReport;
    let updatedClonedChart = cloneDeep(data.charts);
    updatedClonedChart = updatedClonedChart.filter(
      (el) => el?.chartId !== clonedChartId
    );
    updatedClonedChart = [
      ...updatedClonedChart,
      omit(chartData, ['createdBy', 'createdTime'])
    ];
    this.props?.reportContext.setActiveReport({
      ...data,
      charts: updatedClonedChart
    });

    const newLayout = this.props.reportContext?.layout.map((el) => {
      if (el.chart.chartId === clonedChartId) {
        return {
          ...el,
          x: findElement.x,
          y: findElement.y,
          h: findElement.h,
          w: findElement.w,
          chart: chartData
        };
      }
      return el;
    });
    this.props.reportContext.dispatch({
      type: 'updateLayout',
      layout: newLayout
    });
  };

  trackWidget = (activityName, trackData) => {
    useActivityTracker({
      event: activityName,
      data: trackData
    });
  };

  handleSave = async (callback, clone = false, layoutChange = true) => {
    let updatedChartData = cloneDeep({
      ...this.state.chartHeaders,
      ...this.state.chartData
    });
    const { chartId, name, systemChart } = this.props.data || {};
    if (systemChart) {
      this.trackWidget('DEFAULT_WIDGET_CLONE', {
        chartId: chartId,
        name: name
      });
    }
    const clonedChartId = updatedChartData.chartId;
    const formattedMetrics = updatedChartData.metrics.map((met) =>
      met.filterCondition?.operands?.length
        ? met
        : { ...met, filterCondition: null }
    );
    this.updateState('chartLoading', true);
    const noError = await this.validateChartForm();
    if (noError) {
      if (this.props.data?.systemChart || clone) {
        this.setState({ clonedChart: updatedChartData.chartId });
        updatedChartData.chartId = null;
        updatedChartData.parentMetrics = [];
      }
      const { addedItems, updatedItems, deletedItems } = getParsedCrudData(
        updatedChartData.parentMetrics,
        formattedMetrics,
        'metricId'
      );
      updatedChartData.addMetrics = addedItems;
      updatedChartData.updateMetrics = updatedItems;
      updatedChartData.deleteMetrics = deletedItems;

      const type =
        (getPossibleChartTypes(
          updatedChartData.metrics?.length,
          updatedChartData.groupBy?.length
        ).find((type) => type.name === updatedChartData.defaultChartType) &&
          updatedChartData.defaultChartType) ||
        getDefaultChartType(
          updatedChartData.metrics?.length,
          updatedChartData.groupBy?.length
        );

      updatedChartData.defaultChartType = type;

      updatedChartData = omit(updatedChartData, [
        'parentMetrics',
        'metrics',
        'createdBy',
        'createdTime',
        'internalName',
        'systemChart'
      ]);
      this.updateState('submitActionLoading', true);
      if (updatedChartData?.chartId) {
        this.props
          .updateCharts({
            variables: {
              chartId: this.props.data.chartId,
              input: updatedChartData
            }
          })
          .then((res) => {
            this.updateState('submitActionLoading', false);
            if (this.props?.source === 'report' && layoutChange) {
              this.onLayoutChange(clonedChartId, res?.data?.updateChart, clone);
            }
            if (callback) {
              callback();
            }
          })
          .catch(() => {
            this.updateState('submitActionLoading', false);
          });
      } else {
        this.props
          .createNewChart({ variables: { input: updatedChartData } })
          .then((res) => {
            if (res?.data?.createChart) {
              if (callback && clone) {
                callback();
              } else {
                toast('success', 'reports.widget.createWidget.success');
              }
              this.trackWidget('CREATE_WIDGET', {
                chartId: chartId,
                name: name
              });
              if (this.props?.source === 'report' && layoutChange) {
                this.onLayoutChange(
                  clonedChartId,
                  res?.data?.createChart,
                  clone
                );
              }
            }
          });
        this.updateState('submitActionLoading', false);
      }
    }
  };

  checkForSaveDisabled = () => {
    if (
      this.state.chartData.entity === 'AssetSoftwareInventory' &&
      this.state.chartData.metrics
        .map((el) => el.filterCondition)
        .includes(null)
    ) {
      return true;
    }
    return false;
  };

  render() {
    const {
      chartData,
      chartHeaders,
      chartLoading,
      isFullScreen,
      submitActionLoading,
      isTableExpanded,
      errors
    } = this.state;

    const { data, reportContext, source, meta, metaLoading } = this.props;

    const { cloneId = 1 } = reportContext;

    const providerValue = {
      value: chartData,
      headerValue: chartHeaders,
      updateContext: (value) => this.updateState('chartData', value),
      updateHeaderValue: (value) => this.updateState('chartHeaders', value),
      isSameData: this.isSameData,
      isUpdated: this.isUpdated,
      isFullScreen,
      setIsFullScreen: (value) => this.updateState('isFullScreen', value),
      isTableExpanded,
      setIsTableExpanded: (value) => this.updateState('isTableExpanded', value),
      resetData: (value) => this.updateState('chartData', data)
    };

    const sameData = this.isSameData();

    const inDefault =
      reportContext.activeView === DEFAULT_VIEW && cloneId !== null;
    const inCustom =
      reportContext.activeView === CUSTOM_VIEW &&
      (reportContext?.reportViewMode !== VIEW_MODE || source !== 'report') &&
      chartData.systemChart;

    const showBanner =
      !sameData &&
      reportContext.activeView === DEFAULT_VIEW &&
      cloneId === null;

    return (
      <Modal
        className="full-height bg-none full-width br10"
        isFullScreen={isFullScreen}
        isTableExpanded={isTableExpanded}
        showBanner={showBanner}
      >
        <div className="pnone Modal-Wrap full-height">
          <ChartContextProvider value={providerValue}>
            <ChartHeader
              handleSave={this.handleSave}
              originalData={data}
              submitActionLoading={submitActionLoading}
              errors={errors}
              disabledSave={this.checkForSaveDisabled()}
              hideSave={
                reportContext.activeView === DEFAULT_VIEW && cloneId === null
              }
              saveAsOnly={inDefault || inCustom}
              hideDelete={
                reportContext.activeView === DEFAULT_VIEW || !data?.chartId
              }
              fromReport={this.props?.source === 'report'}
            />

            <div className="chartSection">
              {showBanner && (
                <div className="p10">
                  <Banner status="info">
                    <FormattedMessage
                      id="report.widget.banner.default.report.info"
                      values={{
                        a: (chunks) => (
                          <Button
                            plain
                            onClick={() => this.handleSave(null, true, false)}
                          >
                            {chunks}
                          </Button>
                        )
                      }}
                    />
                  </Banner>
                </div>
              )}
              <div className="fx fh">
                <div className="chartContainer">
                  <ChartConfig
                    errors={errors}
                    setErrors={(value) => this.updateState('errors', value)}
                    validateChartForm={this.validateChartForm}
                    meta={meta}
                    metaLoading={metaLoading}
                  />
                </div>
                <div className="minimizer">
                  <div
                    className="minimizeBtn fx jctr actr"
                    onClick={() =>
                      this.updateState('isFullScreen', !isFullScreen)
                    }
                  >
                    <SVGIcon
                      name={isFullScreen ? 'ArrowRight' : 'ArrowLeft'}
                      size="lg"
                    />
                  </div>
                </div>
                <div className="chartOptions">
                  <ChartDetail
                    isFullScreen={isFullScreen}
                    originalData={data}
                    handleSave={this.handleSave}
                    chartLoading={chartLoading}
                    applyFilter={this.props?.source === 'report'}
                    submitActionLoading={submitActionLoading}
                    validateChartForm={this.validateChartForm}
                    meta={meta}
                    numberChartCallback={this.props?.numberChartCallback}
                  />
                </div>
              </div>
            </div>
          </ChartContextProvider>
        </div>
      </Modal>
    );
  }
}

export default injectIntl(withErrorBoundary(ChartSheet));
